import { CheckCircle2, CircleAlert, CircleDashed, GitMerge, GitPullRequest } from 'lucide-react';
import {
  conversationPullRequestList,
  useConversationPullRequests,
} from './conversation-pull-requests';
import {
  checksToneClass,
  type PullRequestChecksSummary,
  pullRequestLabel,
  pullRequestStateClass,
  summarizePullRequestChecks,
} from './pull-request';
import { type PullRequestSessionContext, useSessionPullRequest } from './use-session-pull-request';

const CHECKS: Record<
  'failing' | 'pending' | 'passing',
  Pick<PullRequestChecksSummary, 'tone' | 'label'>
> = {
  failing: { tone: 'failed', label: 'Checks failing' },
  pending: { tone: 'pending', label: 'Checks running' },
  passing: { tone: 'success', label: 'Checks successful' },
};

/**
 * The pull request status chip next to the session title; it opens the pull request. Its
 * branch's one, else the last one bound to the conversation (opened, printed, pushed, linked).
 */
export function PullRequestHeaderChip({
  session,
  enabled,
}: {
  session: PullRequestSessionContext;
  /** The "pull request status in the header" setting. */
  enabled: boolean;
}) {
  const { cockpit, pullRequest } = useSessionPullRequest(session, enabled);
  const local = !session.cloud && !session.preview;
  const bound = useConversationPullRequests(
    local ? session.taskId : null,
    enabled && session.visible,
  );
  const shown = local
    ? conversationPullRequestList(pullRequest, bound)
    : pullRequest
      ? [pullRequest]
      : [];
  const pr = shown.find((entry) => entry.url === pullRequest?.url) ?? shown.at(-1) ?? null;
  if (!pr || !enabled) return null;
  const Icon = pr.state === 'merged' ? GitMerge : GitPullRequest;
  const checks =
    cockpit && pr.url === pullRequest?.url
      ? summarizePullRequestChecks(cockpit)
      : pr.checks === undefined
        ? null
        : CHECKS[pr.checks];
  const Status =
    checks?.tone === 'failed'
      ? CircleAlert
      : checks?.tone === 'pending'
        ? CircleDashed
        : CheckCircle2;
  return (
    <button
      type="button"
      onClick={() => window.open(pr.url, '_blank', 'noopener,noreferrer')}
      title={`${pr.title} · ${pullRequestLabel(pr.state)}${checks ? ` · ${checks.label}` : ''}${
        shown.length > 1 ? ` · ${shown.length} pull requests` : ''
      }`}
      className="inline-flex max-w-64 shrink-0 items-center gap-1.5 whitespace-nowrap rounded px-2 py-1 text-foreground text-xs hover:bg-accent"
      style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
      aria-label={`Open pull request #${pr.number}: ${pullRequestLabel(pr.state)}`}
    >
      <Icon className={`size-3.5 shrink-0 ${pullRequestStateClass(pr.state)}`} />
      <span className="truncate">
        #{pr.number} · {pullRequestLabel(pr.state)}
      </span>
      {checks && (
        <Status
          aria-label={checks.label}
          className={`size-3.5 shrink-0 ${checksToneClass(checks.tone)}`}
        />
      )}
    </button>
  );
}
